import { useEffect, useRef, useState } from 'react';
import {
  Building2,
  Pencil,
  Loader2,
  X,
  Check,
  Upload,
  Trash2,
  Image as ImageIcon,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiClient } from '@/lib/api';
import { useToast } from '@/lib/hooks/use-toast';
import { useAuth } from '@/features/auth/hooks/use-auth';

interface Company {
  id: string;
  name: string;
  slug?: string;
  nit?: string | null;
  address?: string | null;
  country?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  logoUrl?: string | null;
  brandColor?: string | null;
}

/**
 * El color con el que sale la papelería cuando la empresa no eligió ninguno.
 * Es el azul de Prixia — deliberadamente neutro, no el de ningún cliente.
 */
const COLOR_POR_DEFECTO = '#0B3D91';

/** 500 KB. Un logo de verdad pesa entre 20 y 60 KB; arriba de esto es una foto. */
const LOGO_MAX_BYTES = 500 * 1024;

const FIELDS: { key: keyof Company; label: string; type?: string }[] = [
  { key: 'name', label: 'Nombre de la empresa' },
  { key: 'nit', label: 'NIT' },
  { key: 'address', label: 'Dirección' },
  { key: 'country', label: 'País' },
  { key: 'phone', label: 'Teléfono' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'website', label: 'Sitio web' },
];

export function CompanyInfoCard() {
  const { isAdmin, checkAuth, user } = useAuth();
  const esPlataformaAdmin = user?.role === 'PLATFORM_ADMIN';
  const { toast } = useToast();

  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Partial<Company>>({});
  const fileRef = useRef<HTMLInputElement>(null);

  /**
   * Toma el archivo que eligió el admin y lo deja como data URI en el formulario.
   *
   * **Por qué se guarda el archivo y no una URL.** El logo tiene que aparecer en
   * un PDF que se genera en el servidor y en un portal público que se abre sin
   * sesión. Si dependiera de un hosting externo, el día que esa URL se cae la
   * cotización sale sin logo y nadie se entera hasta que el cliente lo dice.
   *
   * Solo PNG y JPEG: un SVG puede traer script adentro y el portal es público.
   * El backend lo vuelve a validar — esto es para avisar acá y no después.
   */
  const elegirLogo = (file: File | undefined) => {
    if (!file) return;

    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      toast({
        title: 'Formato no admitido',
        description: 'El logo tiene que ser PNG o JPG. Un SVG no se puede usar por seguridad.',
        variant: 'destructive',
      });
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      toast({
        title: 'El logo pesa demasiado',
        description: `${Math.round(file.size / 1024)} KB. El máximo es 500 KB — con 60 KB alcanza y sobra.`,
        variant: 'destructive',
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => setForm((p) => ({ ...p, logoUrl: String(reader.result) }));
    reader.onerror = () => toast({ title: 'No se pudo leer el archivo', variant: 'destructive' });
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    apiClient
      .get<Company>('/api/tenant')
      .then((c) => setCompany(c))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const startEdit = () => {
    setForm({ ...company });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        nit: form.nit ?? null,
        address: form.address ?? null,
        country: form.country ?? null,
        phone: form.phone ?? null,
        email: form.email ?? null,
        website: form.website ?? null,
        logoUrl: form.logoUrl ?? null,
        brandColor: form.brandColor ?? null,
      };
      const updated = await apiClient.patch<Company>('/api/tenant', payload);
      setCompany((prev) => ({ ...(prev as Company), ...updated }));
      setEditing(false);
      toast({ title: 'Empresa actualizada', variant: 'success' });
      // Refresca el user del store para que el nombre se vea en toda la app.
      checkAuth();
    } catch (e: any) {
      toast({
        title: 'No se pudo guardar',
        description: e?.response?.data?.message || 'Intentá de nuevo',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-tour="settings-company">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Información de la Empresa</CardTitle>
          </div>
          {isAdmin && !editing && (
            <Button variant="outline" size="sm" onClick={startEdit} disabled={loading}>
              <Pencil className="h-4 w-4 mr-2" />
              Editar
            </Button>
          )}
        </div>
        <CardDescription>Datos de la empresa asociada a tu cuenta</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Cargando…</p>
        ) : editing ? (
          <div className="space-y-4">
            {FIELDS.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label htmlFor={f.key}>{f.label}</Label>
                <Input
                  id={f.key}
                  type={f.type || 'text'}
                  value={(form[f.key] as string) ?? ''}
                  onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
                  disabled={saving}
                />
              </div>
            ))}
            {/* ── Marca ───────────────────────────────────────────────────
                Va al final y separado por una línea: el nombre y el NIT se
                cargan una vez y no se tocan más; el logo y el color son lo que
                el cliente final ve, y merecen su propio espacio. */}
            <div className="space-y-4 border-t pt-4">
              <div>
                <p className="text-sm font-semibold">Marca</p>
                <p className="text-xs text-muted-foreground">
                  Lo que ve el cliente final: aparece en el PDF de la cotización y en el portal de
                  seguimiento.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label>Logo</Label>
                <div className="flex items-center gap-3">
                  <div className="flex h-16 w-32 shrink-0 items-center justify-center rounded-md border bg-white p-2">
                    {form.logoUrl ? (
                      <img
                        src={form.logoUrl}
                        alt="Logo de la empresa"
                        className="max-h-full max-w-full object-contain"
                      />
                    ) : (
                      <ImageIcon className="h-6 w-6 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/png,image/jpeg"
                      className="hidden"
                      onChange={(e) => {
                        elegirLogo(e.target.files?.[0]);
                        // Se limpia para que elegir el mismo archivo dos veces
                        // seguidas vuelva a disparar el onChange.
                        e.target.value = '';
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileRef.current?.click()}
                      disabled={saving}
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      {form.logoUrl ? 'Cambiar' : 'Subir logo'}
                    </Button>
                    {form.logoUrl && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setForm((p) => ({ ...p, logoUrl: null }))}
                        disabled={saving}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Quitar
                      </Button>
                    )}
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  PNG o JPG, hasta 500 KB. Fondo transparente se ve mejor.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="brandColor">Color de la marca</Label>
                <div className="flex items-center gap-2">
                  <input
                    id="brandColor"
                    type="color"
                    value={form.brandColor || COLOR_POR_DEFECTO}
                    onChange={(e) => setForm((p) => ({ ...p, brandColor: e.target.value }))}
                    disabled={saving}
                    className="h-9 w-14 cursor-pointer rounded border bg-transparent p-1"
                  />
                  <Input
                    aria-label="Color en hexadecimal"
                    value={form.brandColor || ''}
                    onChange={(e) => setForm((p) => ({ ...p, brandColor: e.target.value }))}
                    placeholder={COLOR_POR_DEFECTO}
                    disabled={saving}
                    className="w-32 font-mono text-sm"
                  />
                  {form.brandColor && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setForm((p) => ({ ...p, brandColor: null }))}
                      disabled={saving}
                    >
                      Restablecer
                    </Button>
                  )}
                </div>
              </div>

              <VistaPreviaPapeleria
                nombre={form.name || company?.name || 'Tu empresa'}
                nit={form.nit ?? null}
                logoUrl={form.logoUrl ?? null}
                color={form.brandColor || COLOR_POR_DEFECTO}
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button onClick={save} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Guardando…
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Guardar
                  </>
                )}
              </Button>
              <Button variant="ghost" onClick={() => setEditing(false)} disabled={saving}>
                <X className="h-4 w-4 mr-2" />
                Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {FIELDS.map((f) => (
              <div key={f.key}>
                <p className="text-sm font-medium text-muted-foreground">{f.label}</p>
                <p className="text-sm mt-1">{(company?.[f.key] as string) || '—'}</p>
              </div>
            ))}
            <div className="space-y-3 border-t pt-4">
              <p className="text-sm font-semibold">Marca</p>
              <VistaPreviaPapeleria
                nombre={company?.name || 'Tu empresa'}
                nit={company?.nit ?? null}
                logoUrl={company?.logoUrl ?? null}
                color={company?.brandColor || COLOR_POR_DEFECTO}
              />
              {!company?.logoUrl && (
                <p className="text-xs text-muted-foreground">
                  Sin logo cargado: las cotizaciones salen solo con el nombre.
                  {isAdmin && ' Cargalo con "Editar".'}
                </p>
              )}
            </div>

            {/*
              El ID de Tenant es de la plataforma, no del cliente.

              Es el uuid con el que se soporta y se debuggea una empresa; al admin
              del cliente no le dice nada y lo unico que hace es ocupar el lugar
              donde deberia estar su telefono. Se lo deja al superadmin, que es
              quien efectivamente lo copia y lo pega.
            */}
            {esPlataformaAdmin && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">ID de Tenant</p>
                <p className="text-sm mt-1 font-mono text-xs">{company?.id || '—'}</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Cómo se va a ver el encabezado de la cotización.
 *
 * **Por qué existe.** Sin esto, para saber si el logo quedó bien hay que
 * guardar, ir a una cotización, generar el PDF y abrirlo. Cuatro pasos para
 * descubrir que el logo estaba estirado. Acá se ve mientras se elige.
 *
 * Reproduce la banda superior del PDF real: logo a la izquierda, nombre y NIT
 * debajo, y la línea del color de la marca. No es el PDF —es una maqueta— pero
 * alcanza para decidir.
 */
function VistaPreviaPapeleria({
  nombre,
  nit,
  logoUrl,
  color,
}: {
  nombre: string;
  nit: string | null;
  logoUrl: string | null;
  color: string;
}) {
  return (
    <div>
      <p className="mb-1.5 text-xs font-medium text-muted-foreground">
        Así sale el encabezado de la cotización
      </p>
      <div className="overflow-hidden rounded-md border bg-white shadow-sm">
        <div className="flex items-start justify-between gap-4 px-4 pb-3 pt-4">
          <div className="min-w-0">
            {/* 150 × 42 son las mismas medidas que usa el PDF (`fit: [150, 42]`).
                Si acá se viera más grande, el logo se elegiría mirando algo que
                no es lo que después sale impreso. */}
            {logoUrl ? (
              <img
                src={logoUrl}
                alt=""
                className="mb-1.5 max-h-[42px] max-w-[150px] object-contain"
              />
            ) : null}
            <p className="truncate text-sm font-bold text-slate-900">{nombre}</p>
            {nit && <p className="text-[11px] text-slate-500">NIT {nit}</p>}
          </div>
          <div className="shrink-0 text-right">
            <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color }}>
              Cotización de flete
            </p>
            <p className="font-mono text-[11px] text-slate-400">COT-0001</p>
          </div>
        </div>
        {/* La línea de color: es donde más se nota el color de la marca. */}
        <div className="h-1" style={{ backgroundColor: color }} />
        <div className="space-y-1 px-4 py-3">
          <div className="h-2 w-3/4 rounded bg-slate-100" />
          <div className="h-2 w-1/2 rounded bg-slate-100" />
        </div>
      </div>
    </div>
  );
}

export default CompanyInfoCard;
